// usage.js — Pronto Adaptive v2: personal usage signal + time-of-day ranker.
//
// Logs lightweight events to usage_events (fire-and-forget) and ranks the Today
// screen's cards by "what Josh reaches for at this hour." Single-user, in his own
// Supabase rows — a personal signal like the coffee taste loop, not tracking.
// The time-of-day defaults carry the layout on their own; usage only refines it,
// so everything degrades gracefully to the phase default when there's no data.
import { supabase } from "./supabase";

// Fire-and-forget. Analytics must NEVER break the UX, so all errors are swallowed.
export async function logUsage(userId, event, context = {}) {
  if (!userId) return;
  try {
    const now = new Date();
    await supabase.from("usage_events").insert({
      user_id: userId,
      event,
      context,
      hour: now.getHours(),
      dow: now.getDay(),
    });
  } catch {
    // ignore — table may not exist yet, offline, etc.
  }
}

// Recent events for the ranker (last `days`, capped). Returns [] on any failure.
export async function loadUsage(userId, days = 30) {
  if (!userId) return [];
  const since = new Date(Date.now() - days * 86400000).toISOString();
  try {
    const { data } = await supabase
      .from("usage_events")
      .select("event, context, hour, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(1000);
    return data || [];
  } catch {
    return [];
  }
}

// Nav view id → Today metric key.
const VIEW_TO_METRIC = { coffee: "coffee", pantry: "use", tonight: "tonight", shopping: "spesa" };
const METRIC_KEYS = ["coffee", "use", "tonight", "spesa"];

// Time-of-day → default card priority when there's no usage signal yet.
export const PHASE_DEFAULT = {
  morning: ["coffee", "use", "tonight", "spesa"],
  midday: ["use", "spesa", "tonight", "coffee"],
  afternoon: ["tonight", "use", "spesa", "coffee"],
  evening: ["tonight", "spesa", "use", "coffee"],
  night: ["tonight", "coffee", "use", "spesa"],
  late_night: ["tonight", "coffee", "use", "spesa"],
};

// Short human label for the Adaptive banner.
export const PHASE_LABEL = {
  morning: "morning", midday: "midday", afternoon: "afternoon",
  evening: "evening", night: "evening", late_night: "late-night",
};

// Rank the four Today metrics for the current phase/hour. Usage is weighted by
// closeness in hour-of-day and recency (~2-week decay); ties and unseen metrics
// fall back to the phase default order. Pure — pass in already-loaded events.
export function rankMetrics(events, phase, hour = new Date().getHours()) {
  const score = {};
  const now = Date.now();
  for (const e of events || []) {
    const key = VIEW_TO_METRIC[e?.context?.view];
    if (!key) continue;
    const eh = e.hour ?? hour;
    const raw = Math.abs(eh - hour);
    const hourDist = Math.min(raw, 24 - raw);
    const hourW = hourDist <= 2 ? 1 : hourDist <= 4 ? 0.5 : 0.15;
    const ageDays = (now - new Date(e.created_at).getTime()) / 86400000;
    const recW = Math.exp(-ageDays / 14);
    score[key] = (score[key] || 0) + hourW * recW;
  }
  const def = PHASE_DEFAULT[phase] || PHASE_DEFAULT.morning;
  return METRIC_KEYS.slice().sort((a, b) => {
    const d = (score[b] || 0) - (score[a] || 0);
    if (Math.abs(d) > 1e-9) return d;
    return def.indexOf(a) - def.indexOf(b);
  });
}
